import React, { useMemo } from 'react'
import type { FilterFn } from '@tanstack/react-table'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { val2Amount } from '@helpers/utils'
import { getAmounts } from '@pages/Accounting/CostCenter/utils'
import Table from '@pages/Accounting/CostCenter/Details/components/Table'

import type { BudgetAllocationResponse } from '@models/CostCenters'
import type { GlobalBudgetFilter } from '../../types'
import type { Currencies } from '@type/currency'

const columnHelper = createColumnHelper<BudgetAllocationResponse>()

export interface SpendingCategoriesListProps {
  data: BudgetAllocationResponse[]
  currency: Currencies
  startDate?: string
}

const startDateFilter: FilterFn<BudgetAllocationResponse> = (row, columnId, filterOption: GlobalBudgetFilter) => {
  return filterOption.startDate === row.original.start_date
}

export const SpendingCategoriesList: React.FC<SpendingCategoriesListProps> = ({ data, currency, startDate }) => {
  const { t } = useTranslation('accounting', { keyPrefix: 'costCenter.details' })

  const globalFilter: GlobalBudgetFilter = useMemo(() => ({ startDate }), [startDate])

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('accounting_category.name', {
        header: t('categoryLabel'),
        cell: (cell) => cell.getValue() || 'Uncategorized',
      }),
      columnHelper.accessor('amount', {
        header: t('allocatedLabel'),
        cell: (cell) => val2Amount(cell.getValue() || 0, { currency }),
      }),
      columnHelper.accessor(
        (row) => getAmounts(row.amount, row.completed_amount, row.committed_amount, currency).remaining,
        {
          header: t('remainingLabel'),
        },
      ),
      columnHelper.accessor('committed_amount', {
        header: t('commitedLabel'),
        cell: (cell) => val2Amount(cell.getValue() || 0, { currency }),
      }),
      columnHelper.accessor('completed_amount', {
        header: t('spentLabel'),
        cell: (cell) => val2Amount(cell.getValue() || 0, { currency }),
      }),
      columnHelper.accessor(
        (row) => getAmounts(row.amount, row.completed_amount, row.committed_amount, currency).overspent,
        {
          header: t('overspendLabel'),
        },
      ),
    ]
  }, [currency])

  return (
    <Table
      data={data}
      columns={columns}
      globalFilterFn={startDateFilter}
      globalFilter={globalFilter}
      classes={{
        tr: (row) => (row.original.accounting_category ? 'bg-white' : 'bg-indigo-50'),
      }}
    />
  )
}

export default React.memo(SpendingCategoriesList)
